import { MobileFileTypeIcon, MobileFolderIcon, MobileTimeIcon, MobileUploadIcon } from '@/components/Icons';
import Show from '@/components/Show';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useDimensions } from '@/hooks/useDimensions';
import { useSessionStore } from '@/hooks/useSessionStore';
import useGeneratorStore, { GeneratorTypeEnums } from '@/state/generator-store';
import { cn, displayDuration } from '@/utils';
import { estimateMaxKeyboardHeight } from '@/utils/mobile';
import useFlags from '@jenmusic/feature-flag';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useController, UseFormReturn } from 'react-hook-form';
import { useDefaultQuality } from '../../../hooks/useDefaultQuality';
import { useFolderSelect } from '../../../hooks/useFolderSelect';
import { useMobileTextareaStore } from '../../../hooks/useMobileTextareaStore';
import { useQualityControl } from '../../../hooks/useQualityControl';
import { useR3ImageDialog } from '../../../hooks/useR3ImageDialog';
import { useStyleFilter } from '../../../hooks/useStyleFilter';
import MobileTrackControl from '../../GenerateButton/MobileTrackControl';
import CreditCard from '../CreditCard';
import PromptInputContainer from '../SimplePromptInput/PromptInputContainer';
import DurationPicker from './MobileDurationInput';
import { AddFilterButton } from './AddFilterButton';
import { AddFilterIcon } from '../AddFilterIcon';

type Theme = 'dark' | 'light';
type MainType = 'prompt' | 'duration';

interface MobileDetailEffectsProps {
  className?: string;
  theme?: Theme;
  openMobileStyleFilterDialog: () => void;
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  fontSize?: number;
  className?: string;
}

interface StyleProps {
  containerStyle: {
    background: string;
    border: string;
    boxShadow: string;
  };
  textareaContainerStyle: {
    boxShadow: string;
    background: string;
    whiteSpace: string;
    wordWrap: string;
    border: string;
    borderRadius: number;
    minHeight: string;
  };
}

interface TextAreaState {
  isOnFocus: boolean;
  setIsOnFocus: (value: boolean) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  lineCount: number;
}

interface IconSize {
  height: number;
  width: number;
  marginRight: number;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, onClick, fontSize = 16, className }) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn('text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors duration-200', className)}
    onClick={onClick}
  >
    {icon}
    <span className={`text-[${fontSize}px]`}>{label}</span>
  </Button>
);

const useStyles = (theme: Theme = 'dark'): StyleProps => ({
  containerStyle: {
    background: '#212324',
    border: '1px solid #474A4C',
    boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
  },
  textareaContainerStyle: {
    boxShadow: `inset 9px 35px 14px rgba(0, 0, 0, 0.03),
                inset 5px 20px 12px rgba(0, 0, 0, 0.1),
                inset 2px 9px 9px rgba(0, 0, 0, 0.17),
                inset 1px 2px 5px rgba(0, 0, 0, 0.2)`,
    background: `linear-gradient(0deg, #191A1B 0%, #1E2021 100%) padding-box,
                linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0)) border-box`,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    border: '1px solid transparent',
    borderRadius: 6,
    minHeight: '120px',
  },
});

const useTextArea = (): TextAreaState => {
  const [isOnFocus, setIsOnFocus] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lineCount = useMemo(() => {
    const textarea = textareaRef.current?.querySelector('textarea');
    if (!textarea) return 1;

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const lines = Math.floor((textarea.scrollHeight - paddingTop - paddingBottom) / lineHeight);
    return Math.max(1, lines);
  }, [textareaRef.current?.querySelector('textarea')?.value]);

  return {
    isOnFocus,
    setIsOnFocus,
    textareaRef,
    lineCount,
  };
};

const TextAreaComponent: React.FC<{
  promptValue: string;
  promptOnChange: (value: string) => void;
  generatorForm: UseFormReturn<any> | null;
  isOnFocus: boolean;
  setIsOnFocus: (value: boolean) => void;
  lineCount: number;
  className?: string;
  textAreaHeight: number;
}> = ({ promptValue, promptOnChange, generatorForm, isOnFocus, textAreaHeight, setIsOnFocus, lineCount, className }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { setTextareaRef } = useMobileTextareaStore();
  useEffect(() => {
    if (textareaRef.current) {
      setTextareaRef(textareaRef);
    }
  }, [textareaRef.current]);
  return (
    <Textarea
      ref={textareaRef}
      value={promptValue}
      onChange={(e) => {
        promptOnChange(e.target.value);
        generatorForm?.setValue('prompt', e.target.value);
      }}
      style={{
        boxShadow: 'none',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        maskImage: lineCount > 3 ? 'linear-gradient(to top, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0) 100%)' : 'none',
        marginTop: lineCount > 3 ? '-12px' : '0',
        minHeight: `${textAreaHeight}px`,
      }}
      placeholder={!isOnFocus ? `Imagine a song. What does it sound like?` : ''}
      className={cn(
        `bg-transparent border-none text-zinc-400 placeholder:text-zinc-600 focus-visible:ring-0 text-sm resize-none overflow-auto whitespace-pre-wrap break-words`,
        className,
      )}
      autoCorrect="off"
      autoComplete="off"
      autoCapitalize="off"
      onFocus={() => setIsOnFocus(true)}
      onBlur={() => setIsOnFocus(false)}
      spellCheck={false}
    />
  );
};

const HeaderComponent: React.FC<{
  setIsGeneratorDrawerShow: (value: boolean) => void;
}> = ({ setIsGeneratorDrawerShow }) => (
  <div className="flex items-center pb-2">
    <h1 className="flex-1 text-center text-[17px] font-medium">
      <span className="inline-flex items-center gap-1 text-white">Imagine a Track</span>
    </h1>
    <div className="p-2 bg-[#2b2d2f] rounded-full justify-start items-center gap-1.5 inline-flex">
      <button type="button" className="p-1" onClick={() => setIsGeneratorDrawerShow(false)}>
        <X className="w-4 h-4 text-white" />
      </button>
    </div>
  </div>
);

const MobileDetailEffects: React.FC<MobileDetailEffectsProps> = ({ className, theme, openMobileStyleFilterDialog }) => {
  const [showMainType, setShowMainType] = useState<MainType>('prompt');
  const { isOnFocus, setIsOnFocus, textareaRef, lineCount } = useTextArea();
  const styles = useStyles(theme);
  const operatorContainerRef = useRef<HTMLDivElement>(null);

  const { folderName, showFolderDialog } = useFolderSelect();
  const defaultQuality = useDefaultQuality();

  const { session } = useSessionStore();

  const { value, toggleQuality } = useQualityControl(defaultQuality, session);
  const { show: showR3ImageDialog, R3ImageDialogComponent } = useR3ImageDialog(session);
  const { show: showStyleFilter, StyleFilterDialogComponent } = useStyleFilter();
  const { isIPhoneSEorSmaller, isMobileSE } = useDimensions();

  const { generatorType, control, formValues, generatorForm, audioInfo, setIsGeneratorDrawerShow } = useGeneratorStore((state) => ({
    generatorType: state.generatorType,
    control: state.control,
    formValues: state.formValues,
    generatorForm: state.generatorForm,
    audioInfo: state.audioInfo,
    setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
  }));

  const {
    field: { value: promptValue, onChange: promptOnChange },
  } = useController({
    name: 'prompt',
    control,
    defaultValue:
      generatorType === GeneratorTypeEnums.EDITOR ? ((audioInfo as any)?.prompts ?? (audioInfo as any)?.prompt) : formValues?.prompt || '',
  });

  const {
    field: { value: durationValue },
  } = useController({
    name: 'duration',
    control,
  });

  useEffect(() => {
    if (formValues?.duration !== undefined) {
      generatorForm?.setValue('duration', formValues.duration, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [formValues?.duration, generatorForm]);

  const textAreaHeight = () => {
    const windowHeight = window.innerHeight;
    const keyboardHeight = estimateMaxKeyboardHeight();
    return windowHeight - keyboardHeight - 250;
  };

  const renderActionButtons = (iconSize: IconSize, fontSize = 16) => (
    <div className="flex items-center border-t border-[#474A4C] pt-1 mt-2">
      <div className="flex-1 flex items-center justify-center">
        <ActionButton
          icon={<MobileUploadIcon className={`h-${iconSize.height} w-${iconSize.width} mr-${iconSize.marginRight}`} />}
          label="Upload"
          onClick={showR3ImageDialog}
          fontSize={fontSize}
        />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <ActionButton
          icon={<MobileTimeIcon className={`h-${iconSize.height} w-${iconSize.width} mr-${iconSize.marginRight}`} />}
          label={displayDuration(durationValue)}
          onClick={() => setShowMainType('duration')}
          fontSize={fontSize}
        />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <ActionButton
          icon={<MobileFileTypeIcon className={`h-${iconSize.height} w-${iconSize.width} mr-${iconSize.marginRight}`} />}
          label={`.${value}`}
          onClick={toggleQuality}
          fontSize={fontSize}
        />
      </div>
      <div className="flex-1 flex items-center justify-center">
        <ActionButton
          icon={<MobileFolderIcon className={`h-${iconSize.height} w-${iconSize.width} mr-${iconSize.marginRight}`} />}
          label={folderName ?? 'Add'}
          onClick={showFolderDialog}
          fontSize={fontSize}
        />
      </div>
    </div>
  );

  const renderTextArea = (fontSize = 16) => (
    <div
      className={cn(
        'prompt_input_box overflow-hidden inline-flex py-2 px-3 rounded-lg text-white text-lg relative flex-col w-full',
        className,
      )}
      style={styles.textareaContainerStyle as React.CSSProperties}
    >
      <TextAreaComponent
        promptValue={promptValue}
        promptOnChange={promptOnChange}
        generatorForm={generatorForm ?? null}
        isOnFocus={isOnFocus}
        setIsOnFocus={(value) => {
          setIsOnFocus(value);
          // try fix https://jenmusicai.atlassian.net/browse/JEN-875
          if (value) {
            setTimeout(() => {
              window.scrollBy({ top: 74, behavior: 'smooth' });
            }, 100);
          }
        }}
        lineCount={lineCount}
        textAreaHeight={textAreaHeight()}
      />
      {renderActionButtons({ height: 2, width: 3, marginRight: 1 }, fontSize)}
    </div>
  );

  const renderContent = () => {
    const { showStyleFilters } = useFlags();

    const commonContent = (padding: string) => (
      <div className={cn(`w-full flex flex-col bg-[#191A1B] ${padding} rounded-t-2xl relative`, className)} style={styles.containerStyle}>
        <HeaderComponent setIsGeneratorDrawerShow={setIsGeneratorDrawerShow} />
        <Show when={showMainType === 'prompt'}>{renderTextArea(isMobileSE || isIPhoneSEorSmaller ? 12 : 16)}</Show>
        <Show when={showMainType === 'duration'}>
          <DurationPicker setShowMainType={setShowMainType} />
        </Show>
        <div className="flex justify-between mt-2" ref={operatorContainerRef}>
          <PromptInputContainer className="h-20 p-1 gap-2">
            <CreditCard
              width={(operatorContainerRef?.current?.clientWidth ?? 300) - 170}
              height={isMobileSE ? 76 : isIPhoneSEorSmaller ? 66 : undefined}
              onlyShowContent={true}
            />
            <MobileTrackControl />
          </PromptInputContainer>
          {showStyleFilters && (
            <>
              <AddFilterIcon
                onClick={() => {
                  setIsGeneratorDrawerShow(false);
                  openMobileStyleFilterDialog();
                }}
              />
            </>
          )}
        </div>
        <R3ImageDialogComponent />
        <StyleFilterDialogComponent />
      </div>
    );

    if (isMobileSE || isIPhoneSEorSmaller) {
      return commonContent('p-2');
    }

    return commonContent('p-4');
  };

  return renderContent();
};

export default MobileDetailEffects;
